import { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../hooks/reduxToolkit";
import { useClient } from "../providers/supabase";
import { SyncActor, SyncDivergenceError } from "../../lib/sync/syncActor";
import { UnrecoverableSyncError } from "../../lib/sync/unrecoverable";
import { SyncableTables } from "../../lib/sync/action";
import { store } from "../reduxToolkit/store";
import { resetSyncCursor } from "../reduxToolkit/syncSlice";
import { useSentry } from "../providers/sentry";
import { webBackendClient } from "../../lib/platform/webBackendClient";
import { webFileStore } from "../../lib/platform/webFileStore";

/**
 * This listener, when mounted, fires up the SyncActor to run in the background.
 */
export function SyncActorListener() {
  const client = useClient()
  const dispatch = useAppDispatch()
  const {captureException, addBreadcrumb} = useSentry()

  // We use membershipId here because the SyncActor should be restarted whenever the membershipId changes
  const membershipId = useAppSelector(state => state.membership?.membershipId)

  useEffect(() => {
    if (!membershipId) {
      // No need to sync until we've signed in
      return
    }

    // The watermark belongs to the membership that issued it. Carrying one across a
    // membership switch would make the next sync ask only for changes after that point,
    // silently skipping everything older in the membership being switched to.
    dispatch(resetSyncCursor())

    const actor = new SyncActor(client, store, dispatch, SyncableTables, webBackendClient, webFileStore)
    const errorHandler = (error: Error) => {
      captureException(error, {
        tags: {
          category: 'syncActor',
          background: true,
          // A discarded action is a dropped write, not just a failed request — tag
          // it so those can be alerted on and grouped by table and error code.
          ...(error instanceof UnrecoverableSyncError ? {
            sync_discarded: true,
            sync_table: error.table,
            sync_code: error.code || 'unknown',
          } : {}),
          // A table that will not converge means the full reconcile is downloading it
          // whole on every pass — the cost ADR 009 accepted in exchange for a protocol
          // third parties can implement. Tagged so it can be alerted on by table.
          ...(error instanceof SyncDivergenceError ? {
            sync_diverged: true,
            sync_table: error.table,
            sync_passes: error.passes,
          } : {})
        }
      })
      console.error('SyncActor error', error)
    }
    actor.on('error', errorHandler)
    const breadcrumbHandler =
      (message: string) => {
        console.info('SyncActor info', message)
        addBreadcrumb({
          message,
          category: 'syncActor',
          level: 'info',
        })
      }
    actor.on('info', breadcrumbHandler)

    actor.start()

    // Catch up when the app comes back to the foreground.
    //
    // Lives here rather than in SyncActor because `document` does not exist in React
    // Native, and the actor is due to move into packages/shared for the SDK — the same
    // reason appBadge.tsx and nativeAuth.ts own their visibility listeners. Native wires
    // AppState to the same method.
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        actor.catchUp().catch(errorHandler)
      }
    }
    document.addEventListener('visibilitychange', onVisibilityChange)

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange)
      actor.stop()
      actor.off('error', errorHandler)
      actor.off('info', breadcrumbHandler)
    }
  }, [client, dispatch, captureException, membershipId, addBreadcrumb])

  return null;
}
